import {
  levels,
  pino,
  type Logger as PinoLogger,
  type LoggerOptions as PinoLoggerOptions,
  stdSerializers,
} from 'pino';
import { type PrettyOptions, type PrettyStream } from 'pino-pretty';

import * as constants from '../constants';
import * as types from '../types';
import { formatLogMessage, isRecordType, mergeContext, pruneContext } from '../util';

import { AbstractConsoleStrategy } from './ConsoleStrategy';
import { type StrategyOptions } from './Strategy';

const parseLevel = (o: types.LogContext): null | types.LogLevel => {
  if ('level' in o && typeof o.level === 'number') {
    const l = levels.labels[o.level];
    if (types.isLogLevel(l)) {
      return l;
    }
  }
  return null;
};

/**
 * Represents contextual parameters that are used to generate the log message that is issued via the
 * traditional browser {@link console} object when "pino" is configured to log messages to the
 * browser.
 *
 * These contextual parameters are obtained directly from the object, {@link types.LogContext}, that
 * is passed into the browser writer by the "pino" package.
 */
export interface LogMessageContext {
  readonly level: types.LogLevel;
  readonly message: string;
  readonly time: number;
}

const Parsers: {
  [key in keyof LogMessageContext]-?: (o: types.LogContext) => LogMessageContext[key] | null;
} = {
  level: o => {
    const level = parseLevel(o);
    if (level && 'level' in o) {
      delete o.level;
      return level;
    }
    return null;
  },
  message: o => {
    if ('msg' in o && typeof o.msg === 'string') {
      const message = o.msg;
      delete o.msg;
      return message;
    }
    return null;
  },
  time: o => {
    if ('time' in o && typeof o.time === 'number') {
      const time = o.time;
      delete o.time;
      return time;
    }
    return null;
  },
};

/**
 * Options for the {@link PinoStrategy} strategy class.
 */
interface PinoStrategyOptions extends StrategyOptions {
  /**
   * Defines whether or not the underlying {@link PinoLogger} instance should be configured to
   * emit log information to the browser, in cases when the logs are being issued from the client.
   *
   * This is the recommended setting for usage in NextJS applications.
   *
   * @default false
   */
  readonly browser?: boolean;
  /**
   * Additional configuration options, {@link PrettyOptions}, for the underlying "pino-pretty"
   * package's stream, which are merged with the default configuration options,
   * {@link PrettyOptions}.  Options defined via this parameter will override options defined in the
   * default configuration for the "pino-pretty" package.
   */
  readonly prettyOptions?: PrettyOptions;
}

/**
 * A logger strategy, {@link types.IStrategy}, that emits the underlying log messages, context,
 * {@link types.LogContext}, and in some cases, error, {@link types.LoggerCustomError}, via the
 * "pino" package.
 *
 * This strategy cannot be used in runtime environments that do not support usage of the "pino"
 * package (such as on the "edge" or in a NextJS middleware file).  If this strategy is used in
 * those cases, an error will surface.
 */
export class PinoStrategy extends AbstractConsoleStrategy<PinoStrategyOptions> {
  private _pino: null | PinoLogger = null;
  private _prettyStream: null | PrettyStream | undefined = undefined;
  public strategyId: types.StrategyId = types.StrategyId.Pino;

  constructor({
    prettyStream,
    ...options
  }: { readonly prettyStream?: null | PrettyStream } & PinoStrategyOptions) {
    super(options);
    this._prettyStream = prettyStream;
  }

  /**
   * Logs the provided information via the underlying {@link PinoLogger} instance that the strategy
   * is configured for.  This method should not be called directly, but should instead be called
   * via the {@link IStrategy.logWithStrategy} method.
   */
  protected log(
    logger: types.ILoggerConfiguration,
    level: types.LogLevel,
    params: types.ParsedArgs,
  ): void {
    const ctx = pruneContext(params.context);
    const pinoLogger = this.pino(logger);
    if (ctx) {
      return pinoLogger[level](mergeContext(ctx, { error: params.error }), params.message);
    }
    if (params.error) {
      return pinoLogger[level](params.error, params.message);
    }
    if (params.message) {
      pinoLogger[level](params.message);
    }
  }

  /**
   * Returns the "pino" configuration option that allows for logs to be sent to the browser console
   * via the underlying "pino" logger, {@link PinoLogger}.  This configuration will only be used
   * if the strategy is configured with the 'browser' option.
   */
  private browserConfig(logger: types.ILoggerConfiguration) {
    if (this.browser) {
      return {
        asObject: true,
        write: (obj: object) => {
          if (isRecordType(obj)) {
            const objectContext = this.parseLogMessageContext(obj);
            const level = objectContext.level ?? constants.LoggerOptionDefaults.level;
            if (this.isEnabled && this.isLogLevelSatisfied(logger, level) && logger.isEnabled) {
              return super.log(
                logger,
                objectContext.level ?? constants.LoggerOptionDefaults.level,
                {
                  context: obj,
                  message: objectContext.message,
                  time: objectContext.time,
                },
              );
            }
          } else {
            this.logInternally(
              types.LogLevel.Warn,
              'The object being logged via the PinoStrategy is not a record-type and cannot be ' +
                'logged to the browser console.',
            );
          }
        },
      };
    }
    return { disabled: true };
  }

  /**
   * Returns the default configuration for the "pino" package, which is wrapped by the specific
   * logger instance.  This configuration can be overridden by individual logger instances.
   */
  private config(logger: types.ILoggerConfiguration): PinoLoggerOptions {
    return {
      base: logger.context,
      browser: this.browserConfig(logger),
      /**
       * Nest a first-argument {@link Error} under the `error` key rather than pino's default `err`,
       * so it lands at Datadog's reserved `error.message` / `error.stack` attributes.
       */
      errorKey: 'error',
      level: logger.level,
      name: logger.name,
      /**
       * Apply pino's standard error serializer to the `error` key so an {@link Error} nested in a
       * context object is serialized to its type, message, and stack rather than `JSON.stringify`d
       * to an empty object (its fields are non-enumerable).
       */
      serializers: { error: stdSerializers.err },
    };
  }

  /**
   * Initializes the underlying {@link PinoLogger} instance with the provided configuration exposed
   * by the specific extension of the {@link Logger}, in cases where the {@link PinoLogger} instance
   * is not provided to the {@link Logger} on instantiation (which only happens when child loggers
   * are created).
   */
  private initialize(logger: types.ILoggerConfiguration) {
    const stream = this.stream(logger);
    if (stream) {
      return pino(this.config(logger), stream);
    }
    return pino(this.config(logger));
  }

  /**
   * Parses the contextual information that is used to generate the log message from the provided
   * object, {@link types.LogContext}, removing the parsed information from the object in the
   * process.
   *
   * When "pino" is configured to log messages to the browser, it exposes a custom 'writer' property
   * on the configuration.  This "writer" property is a function that accepts a generic
   * {@link object} type as it's argument, and is then responsible for taking that {@link object}
   * type and issuing logs via traditional console methods.
   *
   * In the case of the {@link Logger}, before these traditional console methods are used, the
   * certain properties on the provided {@link object} type need to be removed, and used to generate
   * the log message that will be issued.  That is where this function is needed.
   *
   * @param {object} pinoLogObj
   *   The original object that is included in the arguments provided to the "writer" function when
   *   "pino" is configured to write to the browser.
   *
   * @see {LogMessageContext}
   *
   * @returns {Partial<LogMessageContext>}
   *   The context that is used to generate the log message.
   */
  private parseLogMessageContext(pinoLogObj: types.LogContext): Partial<LogMessageContext> {
    let context: Partial<LogMessageContext> = {};
    for (const k of Object.keys(Parsers)) {
      const key = k as keyof LogMessageContext;
      const value = Parsers[key](pinoLogObj);
      if (value !== null) {
        context = { ...context, [key]: value };
      }
    }
    return context;
  }

  /**
   * Returns the underlying {@link PinoLogger} instance.
   */
  private pino(logger: types.ILoggerConfiguration) {
    this._pino ??= this.initialize(logger);
    return this._pino;
  }

  /**
   * Returns the configuration for the "pino-pretty" package's stream, {@link DestinationStream},
   * that is used in conjunction with the underlying "pino" logger, {@link PinoLogger}, in cases
   * where the logs are being emitted on the server and the runtime environment supports usage of
   * the "pino" package.
   */
  private prettyOptions(logger: types.ILoggerConfiguration): PrettyOptions {
    return {
      colorize: true,
      /* The name, level and time are ignored because they are manually injected into the log
         message via the 'formatLogMessage' method. */
      ignore: 'pid,hostname,name,level,time',
      messageFormat(log, messageKey, levelLabel) {
        return formatLogMessage(
          {
            level:
              types.inferLogLevel(typeof log.level === 'number' ? log.level : levelLabel) ??
              'unknown',
            message: log[messageKey] as string,
          },
          logger,
        );
      },
      minimumLevel: logger.level,
      sync: true,
      translateTime: true,
      ...this.options.prettyOptions,
    };
  }

  /**
   * Attempts to safely initialize the stream {@link PrettyStream} via the dynamic import of the
   * "pino-pretty" package, logging any errors that occur during the import or initialization
   * process.
   *
   * This method is designed to allow this strategy, {@link PinoStrategy}, to be used in a more
   * forgiving manner, just in case the {@link Logger} is being used in a context where the
   * "pino-pretty" package is not available or supported, preventing the surfacing of hard errors
   * when logs are issued via the {@link Logger} instance.
   */
  private safelyInitializePinoPretty(logger: types.ILoggerConfiguration): null | PrettyStream {
    let prettyPackage: unknown;
    try {
      /* eslint-disable-next-line @typescript-eslint/no-require-imports,
         @typescript-eslint/no-require-imports */
      prettyPackage = require('pino-pretty') as unknown;
    } catch (error) {
      this.logInternally(
        types.LogLevel.Error,
        error,
        "There was an error attempting to import the 'pino-pretty' package. Pretty printing via " +
          "the 'pino-pretty' package will be disabled.",
      );
      return null;
    }
    if (typeof prettyPackage === 'function' && 'default' in prettyPackage) {
      const initializer = prettyPackage.default;
      if (typeof initializer !== 'function') {
        this.logInternally(
          types.LogLevel.Error,
          "The imported 'pino-pretty' package's default export does not seem to be an " +
            'initializer function, when it is expected to be so. Pretty printing via the ' +
            "'pino-pretty' package will be disabled.",
        );
        return null;
      }
      const safeInitializer = initializer as (options: PrettyOptions) => PrettyStream;
      try {
        return safeInitializer(this.prettyOptions(logger));
      } catch (error) {
        this.logInternally(
          types.LogLevel.Error,
          error,
          "There was an error attempting to generate the stream for the 'pino-pretty' package. " +
            "Pretty printing via the 'pino-pretty' package will be disabled.",
        );
        return null;
      }
    } else {
      this.logInternally(
        types.LogLevel.Error,
        "The imported 'pino-pretty' package does not seem to have a default export when one is " +
          "expected. Pretty printing via the 'pino-pretty' package will be disabled.",
      );
      return null;
    }
  }

  /**
   * Returns the configured {@link DestinationStream} associated with the "pino-pretty" package
   * in cases where the logger is configured as such and the runtime environment supports usage
   * of the "pino-pretty" package.
   */
  private stream(logger: types.ILoggerConfiguration) {
    if (this._prettyStream === undefined) {
      if (logger.pretty && typeof window === 'undefined') {
        this._prettyStream = this.safelyInitializePinoPretty(logger);
      } else {
        this._prettyStream = null;
      }
    }
    return this._prettyStream;
  }

  /**
   * Whether or not the underlying {@link PinoLogger} should be used to emit log information to the
   * browser when logs are performed in a client environment.  This is the recommended setting
   * when in a development/local environment.
   */
  private get browser() {
    return this.options.browser ?? false;
  }
}
